import { Box, Flex, Grid, GridItem, VStack, Link } from '@chakra-ui/react';
import React from 'react';
import { useNavigate } from 'react-router';
import FaceliftRadioGroup from '@/components/Atoms/FaceliftRadioGroup';
import FaceliftText from '@/components/Atoms/FaceliftText';
import Show from '@/components/Molecules/Show/Show';
import type { ProductField } from '@/components/Templates/Returns/ReturnOrderForm/types';

export interface OrderReturnDetailsViewProps {
	data: {
		clinicName: string;
		ta_contact?: string;
		ta_phone?: string;
		ta_streetaddress?: string;
		ta_city?: string;
		ta_statezip?: string;
		products: Array<{
			ta_tissueid?: string;
			ta_expirationdate?: string;
			ta_datereturnedtransferred?: string;
			ta_newshippingdaterequested?: string;
		}>;
		ta_hastheproductbeeninyourpossessionupon?: boolean;
		ta_hastheproductbeenstoredaccordingtothemanu?: boolean;
		ta_hastheproductbeensecurelystoredbyauthoriz?: boolean;
		ta_hasanyproductbeenusedoropen?: boolean;
		ta_istheproductintheoriginalunmarkedandundam?: boolean;
		ta_commentsclarifications?: string;
		orderRequest?: {
			ta_orderrequestid?: string;
			ta_name?: string;
			navigateTo?: string;
		};
	};
}

const OrderReturnDetailsView: React.FC<OrderReturnDetailsViewProps> = ({ data }) => {
	const navigate = useNavigate();

	// Helper component for data field (label-value pair)
	const DataField: React.FC<{ label: string; value: string | undefined }> = ({ label, value }) => (
		<GridItem>
			<FaceliftText fontSize="sm" fontWeight="semibold">
				{label}
			</FaceliftText>
			<FaceliftText fontSize="sm" fontWeight="normal" mt={1}>
				{value || ''}
			</FaceliftText>
		</GridItem>
	);

	// Helper component for clickable link field
	const LinkField: React.FC<{ label: string; value: string | undefined; navigateTo?: string }> = ({ label, value, navigateTo }) => {
		const handleClick = () => {
			if (navigateTo) {
				navigate(navigateTo);
			}
		};

		return (
			<GridItem>
				<FaceliftText fontSize="sm" fontWeight="semibold">
					{label}
				</FaceliftText>
				<Show when={!!navigateTo}>
					<Link
						as="button"
						onClick={handleClick}
						color="brand.600"
						textDecoration="none"
						cursor="pointer"
						fontSize="sm"
						fontWeight="normal"
						mt={1}
						_hover={{ color: 'brand.500' }}
					>
						{value || ''}
					</Link>
				</Show>
				<Show when={!navigateTo}>
					<FaceliftText fontSize="sm" fontWeight="normal" mt={1}>
						{value || ''}
					</FaceliftText>
				</Show>
			</GridItem>
		);
	};

	const radioItems = [
		{ label: 'Yes', value: 'true' },
		{ label: 'No', value: 'false' },
	];

	const complianceQuestions = [
		{
			question: 'Has the product been in your possession since receiving it?',
			value: data.ta_hastheproductbeeninyourpossessionupon,
		},
		{
			question: "Has the product been stored according to the manufacturer's specifications?",
			value: data.ta_hastheproductbeenstoredaccordingtothemanu,
		},
		{
			question: 'Has the product been securely stored by authorized clinicians?',
			value: data.ta_hastheproductbeensecurelystoredbyauthoriz,
		},
		{
			question: 'Has any product been used or open?',
			value: data.ta_hasanyproductbeenusedoropen,
		},
		{
			question: 'Is the product in the original unmarked and undamaged packaging?',
			value: data.ta_istheproductintheoriginalunmarkedandundam,
		},
	];

	return (
		<Box>
			{/* Customer Information - 3 columns */}
			<Grid templateColumns="repeat(3, 1fr)" gap={6} mb={6}>
				<DataField label="Customer Name" value={data.clinicName} />
				<DataField label="Street Address" value={data.ta_streetaddress} />
				<DataField label="City" value={data.ta_city} />
				<DataField label="Zip/Postal Code" value={data.ta_statezip} />
				<DataField label="Phone" value={data.ta_phone} />
				<DataField label="Contact" value={data.ta_contact} />
				{/* Order Request - clickable link if available */}
				{data.orderRequest?.ta_orderrequestid && data.orderRequest?.ta_name && (
					<LinkField
						label="Order Request"
						value={data.orderRequest.ta_name}
						navigateTo={data.orderRequest.navigateTo}
					/>
				)}
			</Grid>

			{/* Products - Each product in its own row with 4 columns */}
			{data.products.map((product, index) => (
				<Grid key={index} templateColumns="repeat(4, 1fr)" gap={6} mb={6}>
					<DataField label="Tissue ID" value={product.ta_tissueid} />
					<DataField label="Expiration Date" value={product.ta_expirationdate} />
					<DataField label="Date Return/Transferred" value={product.ta_datereturnedtransferred} />
					<DataField label="New Shipping Date" value={product.ta_newshippingdaterequested} />
				</Grid>
			))}

			{/* Compliance Checklist */}
			<VStack gap={3} align="stretch" mb={6} mt={8}>
				{complianceQuestions.map((item, index) => (
					<Flex key={index} align="center" gap={14}>
						<FaceliftText fontSize="sm" fontStyle="italic" minW="600px">
							{item.question}
						</FaceliftText>
					<Box cursor="default" pointerEvents="none">
						<FaceliftRadioGroup
							value={item.value === true ? 'true' : item.value === false ? 'false' : null}
							onValueChange={() => {}}
							items={radioItems}
							gap={2}
						/>
					</Box>
					</Flex>
				))}
			</VStack>

			{/* Comments Section */}
			{data.ta_commentsclarifications && (
				<Box mt={6}>
					<FaceliftText fontSize="sm" fontWeight="semibold" mb={2}>
						Comments
					</FaceliftText>
					<FaceliftText fontSize="sm">
						{data.ta_commentsclarifications}
					</FaceliftText>
				</Box>
			)}
		</Box>
	);
};

export default OrderReturnDetailsView;

